import { cva } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { cn } from '../../lib/cn'
import { Icon } from '../icon/icon'
import { Status, type StatusTone } from '../status/status'

/**
 * The table cell set (235:5091): Type = Topic / Content / ID & Name.
 *
 * Figma draws the bottom Stroke inside the cell, so the CSS border takes its 1px
 * out of the bottom padding (11px + 1px) to keep the drawn 42px and 69px heights
 * and the text where the Figma puts it.
 *
 * Two shapes come from the Work Orders list (321:25525) rather than the set:
 * `size="md"` is its 48px Topic with 12px sides (321:25527), against the set's
 * 42px with 16px sides; and `type="status"` is its "Status Content" cell
 * (321:25615), a Status pill on the 69px Content row; the pill keeps its natural
 * width and may overrun the cell's right padding, as "Generate Distribution
 * Package" does by 3px in its 246px cell (321:25617). The same screen sets its
 * Asset type Content cells (321:25590) in Body SemiBold Dark: `emphasis`.
 *
 * The list's last two columns are also its own: `type="designer"` is the 32px
 * `user` glyph on its Accent light disc with 12px all round (321:25642), or the
 * word "Unassigned" as a Content cell (321:25640); `type="deadline"` is a pill with
 * 12px sides, 2px top and bottom and the 8px radius on a cell with 8px sides
 * (321:25685): Error light with Error Label Medium when overdue (321:25686), white
 * with Light dark Label otherwise (321:25692). The Deadline heading has 7px sides
 * (321:25683): Topic `inset="narrow"`.
 */
export const tableCellVariants = cva('flex border-b border-atlas-stroke bg-atlas-light pt-3 pb-2.75 font-atlas-sans', {
	variants: {
		type: {
			topic: 'items-center justify-center px-4 text-atlas-label-bold text-atlas-pure-black',
			content: 'h-17.25 items-center justify-center px-3 text-atlas-body text-atlas-light-dark',
			'id-name': 'flex-col justify-center gap-0.5 px-3',
			status: 'h-17.25 items-center px-3',
			designer: 'h-17.25 items-center px-3',
			deadline: 'h-17.25 items-center px-2',
		},
		inset: {
			default: '',
			narrow: '',
		},
		size: {
			sm: '',
			md: '',
		},
		emphasis: {
			false: '',
			true: '',
		},
	},
	compoundVariants: [
		{ type: 'topic', size: 'md', className: 'h-12 px-3' },
		{ type: 'topic', inset: 'narrow', className: 'px-1.75' },
		{ type: 'content', emphasis: true, className: 'text-atlas-body-semibold text-atlas-dark' },
	],
	defaultVariants: { type: 'topic', size: 'sm', emphasis: false, inset: 'default' },
})

type CellBase = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
	className?: string
	/** Topic only: `sm` is the set's 42px heading, `md` the Work Orders list's 48px one. */
	size?: 'sm' | 'md'
}

/** Topic (a column heading) or Content (a plain value): one truncated line. */
interface TextCellProps extends CellBase {
	type?: 'topic' | 'content'
	label: string
	/** Content only: Body SemiBold in Dark, as the Work Orders list's Asset type column. */
	emphasis?: boolean
	/** Topic only: `narrow` is the Deadline heading's 7px sides (321:25683). */
	inset?: 'default' | 'narrow'
	/** Topic only: the column's sort, shown as an arrow after the label; not drawn in the Figma. */
	sortDirection?: 'ascending' | 'descending'
}

/** ID & Name: the record's code over its name, each truncated to one line. */
interface IdNameCellProps extends CellBase {
	type: 'id-name'
	/** The record's code, e.g. a work-order number. */
	code: string
	name: string
}

/** Status: a Status pill, e.g. a work order's workflow status. */
interface StatusCellProps extends CellBase {
	type: 'status'
	/** The status's display text, e.g. the workflow status's configured label. */
	label: string
	tone?: StatusTone
}

/** Designer: the `user` glyph when someone is assigned, the unassigned text otherwise. */
interface DesignerCellProps extends CellBase {
	type: 'designer'
	/** The assigned designer's name, the glyph's accessible name; omit it when unassigned. */
	name?: string
	unassignedLabel?: string
}

/** Deadline: the formatted date in a pill, red when flagged. */
interface DeadlineCellProps extends CellBase {
	type: 'deadline'
	/** The formatted date, e.g. "21 Oct 2026". Formatting is the caller's. */
	label: string
	/** Flags the deadline (passed or at risk, the caller decides): Error light fill and Error Medium text. */
	overdue?: boolean
}

export type TableCellProps = TextCellProps | IdNameCellProps | StatusCellProps | DesignerCellProps | DeadlineCellProps

const deadlinePillClass = {
	overdue: 'bg-atlas-error-light text-atlas-label-medium text-atlas-error',
	due: 'bg-atlas-light text-atlas-label text-atlas-light-dark',
}

function DesignerCell({ type, size, name, unassignedLabel = 'Unassigned', className, ...rest }: DesignerCellProps) {
	if (!name) return <TableCell {...rest} type="content" size={size} label={unassignedLabel} className={className} />
	return (
		<div {...rest} className={cn(tableCellVariants({ type, size }), className)}>
			<Icon name="user" label={name} />
		</div>
	)
}

function DeadlineCell({ type, size, label, overdue = false, className, ...rest }: DeadlineCellProps) {
	return (
		<div {...rest} className={cn(tableCellVariants({ type, size }), className)}>
			<span
				data-overdue={overdue || undefined}
				className={cn(
					'min-w-0 truncate rounded-atlas-sm px-3 py-0.5',
					overdue ? deadlinePillClass.overdue : deadlinePillClass.due,
				)}
			>
				{label}
			</span>
		</div>
	)
}

/**
 * The content box of one table cell, with its padding, Stroke rule and text
 * styles. It renders a plain block, so the surrounding table supplies the cell
 * semantics (a `td`, or react-aria's `Cell`); the width is the column's.
 */
export function TableCell(props: TableCellProps) {
	if (props.type === 'id-name') {
		const { type, size, code, name, className, ...rest } = props
		return (
			<div {...rest} className={cn(tableCellVariants({ type, size }), className)}>
				<span className="w-full truncate text-atlas-label-medium text-atlas-light-dark">{code}</span>
				<span className="w-full truncate text-atlas-body text-atlas-dark">{name}</span>
			</div>
		)
	}
	if (props.type === 'designer') return <DesignerCell {...props} />
	if (props.type === 'deadline') return <DeadlineCell {...props} />
	if (props.type === 'status') {
		const { type, size, label, tone, className, ...rest } = props
		return (
			<div {...rest} className={cn(tableCellVariants({ type, size }), className)}>
				<Status label={label} tone={tone} />
			</div>
		)
	}
	const { type = 'topic', size, emphasis, inset, label, sortDirection, className, ...rest } = props
	return (
		<div {...rest} className={cn(tableCellVariants({ type, size, emphasis, inset }), className)}>
			<span className="min-w-0 flex-1 truncate">{label}</span>
			{sortDirection ? (
				<Icon name={sortDirection === 'ascending' ? 'arrow-up' : 'arrow-down'} className="size-4" />
			) : null}
		</div>
	)
}
